import clsx from 'clsx'
import React, { useContext, useMemo, useRef } from 'react'

import { ProductContext } from '@Components/Product/Context'
import SliderProvider from '@Components/Product/Slider'
import { Breadcrumbs } from '@Domains/Product/Features/Display/Breadcrumbs/Breadcrumbs'
import { getGalleryAssets } from '@Domains/Product/Features/Display/GalleryAssets'
import { Info } from '@Domains/Product/Features/Display/Info/Info'
import ProductSliders from '@Domains/Product/Features/Display/ProductSliders/ProductSliders'
import ProductTactics from '@Domains/Product/Features/Tactics/ProductTactics'
import { PagePresentation } from '@Generated/graphql'
import { AnchorItemInjections } from '@Globals/Injections/Client'
import { MetadataContext } from '@Globals/Metadata'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './Display.module.scss'

export const ProductDisplay = ({
  id,
  presentation = PagePresentation.Full,
}: {
  id?: string
  presentation?: PagePresentation
}) => {
  const product = useContext(ProductContext)
  const assets = useMemo(() => getGalleryAssets(product), [product])
  const scrollToRef = useRef<HTMLDivElement>(null)
  const { page: { breadcrumbs = [] } = {} } = useContext(MetadataContext)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const useStickyGalleryV1 = useSplitTest('sticky_gallery_v1', 'b')

  if (!product) {
    return null
  }

  // TODO: render according to spec
  if (presentation === PagePresentation.Sidebar) {
    return (
      <div id={id}>
        <Info
          reviewWrapperRef={scrollToRef}
        />
      </div>
    )
  }

  return (
    <div className={clsx(styles.wrapper, useStickyGalleryV1 && styles.useStickyGalleryV1)} id={id}>
      <div className={styles.content}>
        <div className={styles.layout} ref={wrapperRef}>
          <div className={styles.slider}>
            <div className={styles.sticky}>
              <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumbs} />
              <SliderProvider slides={assets}>
                <ProductSliders />
              </SliderProvider>
            </div>
          </div>
          <div className={styles.info} data-intersection-id="product-display-block">
            <Info
              reviewWrapperRef={scrollToRef}
            />
          </div>
        </div>

        <AnchorItemInjections anchor="tactics.silver.before" />
        <ProductTactics group="silver" />
        <AnchorItemInjections anchor="tactics.silver.after" />
      </div>
    </div>
  )
}
